import { Component } from '@theme/component';

/** Prototype presentation only. The existing PDP owns variants, prices and quantity rules. */
class AtelierOptions extends Component {
  #root; #data; #listeners; #tip; #lastColour; #statusObserver; #mobileFrame;
  #tipChip; #tipImage; #tipCode; #tipName; #tipShowTimer; #tipHideTimer; #touchInput = false;
  #tipLoading; #tipError;
  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#hideTip(); this.#tip?.remove(); this.#statusObserver?.disconnect();
    this.#root = this.closest('fivepoint-pdp');
    if (!this.#root) return;
    this.#data = JSON.parse(this.#root.querySelector('[data-fp-data]').textContent);
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#root.addEventListener('atelier:selection', this.#sync, options);
    this.#root.querySelector('[data-atelier-colours-more]')?.addEventListener('click', () => {
      const box = this.#root.querySelector('[data-atelier-colour-box]');
      this.#collapse(!box.hasAttribute('data-atelier-collapsed'));
    }, options);
    const quantity = this.#root.querySelector('[data-atelier-quantity]');
    const applyQuantity = report => {
      quantity.setCustomValidity('');
      if (!Number.isInteger(quantity.valueAsNumber) || !quantity.checkValidity()) {
        if (report) { quantity.setCustomValidity(this.dataset.quantityError); quantity.reportValidity(); }
        return;
      }
      const current = Number(this.#root.querySelector('[data-fp-quantity]').value);
      if (quantity.valueAsNumber !== current) this.#root.stepQty((quantity.valueAsNumber - current) / Number(quantity.step));
    };
    quantity?.addEventListener('input', () => applyQuantity(false), options);
    quantity?.addEventListener('change', () => applyQuantity(true), options);
    quantity?.addEventListener('keydown', event => {
      if (event.key === 'Enter') { event.preventDefault(); quantity.dispatchEvent(new Event('change')); }
    }, options);
    this.#tip = document.createElement('div');
    this.#tip.className = 'fp-atelier-colour-tip'; this.#tip.hidden = true;
    this.#tip.innerHTML = '<div class="fp-atelier-colour-tip__content"><div class="fp-atelier-colour-tip__image"><img width="560" height="560" decoding="async" alt="" hidden><span class="fp-atelier-colour-tip__loading" hidden></span><span class="fp-atelier-colour-tip__error" hidden></span><span class="fp-atelier-colour-tip__code"></span></div><div class="fp-atelier-colour-tip__name"></div></div>';
    this.#tipImage = this.#tip.querySelector('img');
    this.#tipLoading = this.#tip.querySelector('.fp-atelier-colour-tip__loading');
    this.#tipError = this.#tip.querySelector('.fp-atelier-colour-tip__error');
    this.#tipError.textContent = this.dataset.imageError;
    this.#tipCode = this.#tip.querySelector('.fp-atelier-colour-tip__code');
    this.#tipName = this.#tip.querySelector('.fp-atelier-colour-tip__name');
    // Each radio already has the full accessible colour name; this is a visual duplicate.
    this.#tip.setAttribute('aria-hidden', 'true'); document.body.append(this.#tip);
    const grid = this.#root.querySelector('.fp-chips');
    grid?.addEventListener('pointerover', this.#showTip, options);
    grid?.addEventListener('focusin', this.#showTip, options);
    grid?.addEventListener('pointerout', this.#leaveTip, options);
    grid?.addEventListener('pointerleave', this.#queueHideTip, options);
    grid?.addEventListener('focusout', this.#leaveTip, options);
    grid?.addEventListener('pointerdown', event => {
      this.#touchInput = event.pointerType === 'touch'; this.#hideTip();
    }, options);
    this.#tip.addEventListener('pointerenter', () => clearTimeout(this.#tipHideTimer), options);
    this.#tip.addEventListener('pointerleave', this.#queueHideTip, options);
    document.addEventListener('keydown', event => {
      this.#touchInput = false;
      if (event.key === 'Escape') this.#hideTip();
    }, options);
    grid?.addEventListener('scroll', this.#hideTip, { ...options, passive: true });
    document.addEventListener('scroll', this.#onScroll, { ...options, passive: true, capture: true });
    window.addEventListener('resize', this.#hideTip, options);
    window.addEventListener('resize', this.#onScroll, options);
    const status = this.#root.querySelector('[data-atelier-status]');
    if (status) {
      this.#statusObserver = new MutationObserver(() => {
        const purchase = status.closest('fivepoint-atelier-purchase');
        const added = status.textContent === purchase.dataset.added;
        this.#root.toggleAttribute('data-atelier-added', added);
      });
      this.#statusObserver.observe(status, { childList: true, characterData: true, subtree: true });
    }
    this.#collapse(this.#chips().length > 20);
    this.#sync();
  }
  #chips() { return [...this.#root.querySelectorAll('.fp-chips [data-fp-chip]:not(.fp-chip--other)')]; }
  #name(chip) {
    const label = chip.querySelector('u')?.textContent.trim() || '';
    return label;
  }
  #collapse(collapsed) {
    const box = this.#root.querySelector('[data-atelier-colour-box]');
    const button = this.#root.querySelector('[data-atelier-colours-more]');
    const many = this.#chips().length > 20;
    if (!box) return;
    box.toggleAttribute('data-atelier-collapsed', many && collapsed);
    if (!button) return;
    button.hidden = !many; button.setAttribute('aria-expanded', String(!collapsed));
    button.textContent = collapsed ? button.dataset.expand : button.dataset.collapse;
    if (collapsed) {
      const chips = this.#chips();
      const hiddenCurrent = chips.slice(20).find(chip => chip.tabIndex === 0);
      if (hiddenCurrent) {
        hiddenCurrent.tabIndex = -1;
        const first = chips.slice(0, 20).find(chip => !chip.disabled);
        if (first) first.tabIndex = 0;
      }
    }
  }
  #sync = event => {
    const selected = this.#root.querySelector('.fp-chips [aria-checked="true"]');
    const colour = this.#data.colours.find(value => String(value.n) === selected?.dataset.fpChip);
    const kind = event?.detail?.kind || this.#root.querySelector('[data-fp-foot]')?.dataset.tier;
    const name = selected ? this.#name(selected) : this.dataset.choose;
    const now = this.#root.querySelector('[data-atelier-colour-now]');
    if (now) now.textContent = name;
    for (const chip of this.#root.querySelectorAll('.fp-chips [data-fp-chip]')) chip.setAttribute('aria-label', this.#name(chip));
    if (selected?.dataset.fpChip !== this.#lastColour && this.#chips().indexOf(selected) >= 20) this.#collapse(false);
    this.#lastColour = selected?.dataset.fpChip;
    const quantity = this.#root.querySelector('[data-atelier-quantity]');
    const amount = Number(this.#root.querySelector('[data-fp-quantity]')?.value);
    if (quantity) {
      const cap = Math.min(this.#data.maxQty, colour?.max ?? this.#data.maxQty);
      const validRule = Number.isInteger(colour?.min) && colour.min > 0 && Number.isInteger(colour?.step) && colour.step > 0 && Number.isInteger(cap) && cap >= colour.min;
      quantity.disabled = kind !== 'sample' || !validRule || !!colour?.other;
      if (validRule) {
        quantity.min = String(colour.min); quantity.step = String(colour.step);
        quantity.max = String(colour.min + Math.floor((cap - colour.min) / colour.step) * colour.step);
      }
      quantity.value = Number.isSafeInteger(amount) && amount > 0 ? String(amount) : '';
      quantity.setCustomValidity('');
    }
    const yards = this.#root.querySelector('[data-atelier-yards]');
    if (yards) yards.textContent = Number.isSafeInteger(amount) && amount > 0 ? `${amount} ${amount === 1 ? this.dataset.metre : this.dataset.metres} ≈ ${(amount / 0.9144).toFixed(2)} ${this.dataset.yards}` : '';
    const mobile = this.#root.querySelector('[data-atelier-mobile-label]');
    if (mobile) mobile.textContent = name;
    const mobileButton = this.#root.querySelector('[data-atelier-mobile-add]');
    if (mobileButton) mobileButton.textContent = this.#root.querySelector('[data-fp-add-label]')?.textContent || '';
    const mobilePrice = this.#root.querySelector('[data-atelier-mobile-price]');
    if (mobilePrice) mobilePrice.textContent = this.#root.querySelector('.fp-commerce-price')?.textContent.trim() || '';
    const swatch = this.#root.querySelector('[data-atelier-mobile-swatch]');
    if (swatch) {
      const image = selected?.querySelector('img');
      swatch.replaceChildren(); swatch.classList.toggle('is-empty', !image);
      if (image) { const copy = image.cloneNode(); copy.alt = ''; swatch.append(copy); }
    }
    this.#onScroll();
  };
  // Native scrolling and anchor jumps can skip the button without changing an
  // IntersectionObserver's false state. Also listen to the theme's inner scroller.
  #onScroll = event => {
    if (event?.target instanceof Node && this.#tip?.contains(event.target)) return;
    this.#hideTip();
    if (this.#mobileFrame) return;
    this.#mobileFrame = requestAnimationFrame(() => {
      this.#mobileFrame = null;
      const bar = this.#root.querySelector('[data-atelier-mobile]');
      const button = this.#root.querySelector('[data-fp-add] button');
      if (bar && button) bar.hidden = innerWidth > 767 || button.getBoundingClientRect().bottom >= 0 || this.#root.querySelector('[data-fp-foot]').dataset.mode === 'enquiry';
    });
  };
  #showTip = event => {
    if (event.pointerType === 'touch') { this.#touchInput = true; this.#hideTip(); return; }
    if (event.type === 'pointerover') this.#touchInput = false;
    if (this.#touchInput) return;
    const chip = event.target instanceof Element ? event.target.closest('[data-fp-chip]') : null;
    if (!chip || chip.classList.contains('fp-chip--other')) { this.#queueHideTip(); return; }
    clearTimeout(this.#tipHideTimer);
    if (chip === this.#tipChip && (!this.#tip.hidden || this.#tipShowTimer)) return;
    const delay = this.#tip.hidden && event.type !== 'focusin' ? 200 : 0;
    this.#hideTip(); this.#tipChip = chip;
    this.#tipShowTimer = setTimeout(() => {
      this.#tipShowTimer = null;
      if (!chip.isConnected || this.#tipChip !== chip) return;
      const code = chip.dataset.fpChip.trim();
      const name = chip.querySelector('u')?.textContent.trim() || '';
      const src = chip.querySelector('img')?.dataset.atelierPreviewSrc;
      this.#tipCode.textContent = name;
      this.#tipName.textContent = ''; this.#tipName.hidden = true;
      this.#tip.toggleAttribute('data-text-only', !src);
      this.#loadTipImage(src, name);
      this.#tip.hidden = false; this.#positionTip(chip);
    }, delay);
  };
  #loadTipImage(src, alt) {
    // A fresh element cannot retain the previous colour's decoded bitmap.
    const image = document.createElement('img');
    image.width = 560; image.height = 560; image.decoding = 'async'; image.alt = alt; image.hidden = true;
    this.#tipImage.replaceWith(image); this.#tipImage = image;
    this.#tipError.hidden = true; this.#tipLoading.hidden = !src;
    if (!src) return;
    const finish = loaded => {
      if (this.#tipImage !== image || !image.hasAttribute('src') || !this.#tipChip) return;
      image.onload = null; image.onerror = null;
      this.#tipLoading.hidden = true; image.hidden = !loaded; this.#tipError.hidden = loaded;
    };
    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = src;
    if (image.complete) finish(image.naturalWidth > 0);
  }
  #positionTip(chip) {
    const edge = 8, gap = 12;
    this.#tip.style.setProperty('--fp-tip-max-height', `${innerHeight - edge * 2}px`);
    const box = chip.getBoundingClientRect();
    const width = this.#tip.offsetWidth, height = this.#tip.offsetHeight;
    const above = box.top - gap - edge, below = innerHeight - box.bottom - gap - edge;
    const placement = above >= height || above >= below ? 'above' : 'below';
    const room = placement === 'above' ? above : below;
    if (room < 48) { this.#hideTip(); return; }
    this.#tip.style.setProperty('--fp-tip-max-height', `${room}px`);
    const left = Math.max(edge, Math.min(innerWidth - width - edge, box.left + (box.width - width) / 2));
    this.#tip.dataset.placement = placement;
    this.#tip.style.left = `${left}px`;
    this.#tip.style.top = `${placement === 'above' ? box.top - Math.min(height, room) - gap : box.bottom + gap}px`;
    this.#tip.style.setProperty('--fp-tip-arrow-x', `${Math.max(14, Math.min(width - 14, box.left + box.width / 2 - left))}px`);
  }
  #leaveTip = event => {
    if (event.relatedTarget instanceof Node && (this.#tipChip?.contains(event.relatedTarget) || this.#tip?.contains(event.relatedTarget))) return;
    this.#queueHideTip();
  };
  #queueHideTip = () => {
    clearTimeout(this.#tipShowTimer); clearTimeout(this.#tipHideTimer);
    this.#tipShowTimer = null;
    this.#tipHideTimer = setTimeout(this.#hideTip, 200);
  };
  #hideTip = () => {
    clearTimeout(this.#tipShowTimer); clearTimeout(this.#tipHideTimer);
    this.#tipShowTimer = null; this.#tipHideTimer = null;
    this.#tipChip = null;
    if (this.#tipImage) {
      this.#tipImage.onload = null; this.#tipImage.onerror = null;
      this.#tipImage.hidden = true; this.#tipImage.removeAttribute('src');
    }
    if (this.#tipLoading) this.#tipLoading.hidden = true;
    if (this.#tip) this.#tip.hidden = true;
  };
  disconnectedCallback() {
    super.disconnectedCallback(); this.#hideTip(); this.#listeners?.abort(); this.#statusObserver?.disconnect(); this.#tip?.remove(); cancelAnimationFrame(this.#mobileFrame);
  }
}
if (!customElements.get('fivepoint-atelier-options')) customElements.define('fivepoint-atelier-options', AtelierOptions);
